import { FC } from "react";
import { Box, Button, Card, Chip, IconButton, Stack, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";

import { CustomTextField } from "src/components/atom/CustomTextfield";
import { contacts } from "src/components/organism/constant";

const Contact: FC = () => {
  const { t } = useTranslation();

  return (
    <Stack spacing={5} py={{ xs: 3, md: 5 }}>
      <SectionHeader title={t("contactMe")} eyebrow="Contact" />

      <Stack alignItems="center" spacing={1} textAlign="center">
        <Typography level="h3" sx={{ color: "primary.500" }}>
          {t("haveAnyQuestions")}
        </Typography>
        <Typography level="body-md" sx={{ color: "text.tertiary" }}>
          {t("imAtYourService")}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {contacts.map(({ icon: ItemIcon, title, text, link }, index) => (
          <Card
            key={`${title}-${index}`}
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              alignItems: "center",
              textAlign: "center",
              bgcolor: "background.surface",
              backdropFilter: "blur(14px)",
              transition: "0.25s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "lg",
                borderColor: "primary.300",
              },
            }}
          >
            <IconButton
              component="a"
              href={link}
              target={t(title) === "Office" ? "_blank" : "_parent"}
              color="primary"
              variant="soft"
              sx={{ width: 56, height: 56, borderRadius: "20px" }}
            >
              <ItemIcon style={{ fontSize: 30 }} />
            </IconButton>

            <Typography level="title-md" noWrap>
              {t(title)}
            </Typography>
            <Typography
              level="body-sm"
              noWrap
              sx={{ maxWidth: "100%", color: "text.tertiary" }}
            >
              {t(text)}
            </Typography>
          </Card>
        ))}
      </Box>

      <Card
        variant="outlined"
        sx={{
          p: { xs: 2.5, md: 4 },
          bgcolor: "background.surface",
          backdropFilter: "blur(16px)",
          boxShadow: "sm",
        }}
      >
        <Stack alignItems="center" spacing={1} textAlign="center" sx={{ mb: 3 }}>
          <Typography level="h3" sx={{ color: "primary.500" }}>
            {t("sendMeAnEmail")}
          </Typography>
          <Typography level="body-md" sx={{ color: "text.tertiary" }}>
            {t("veryResponsiveToMessage")}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <CustomTextField placeholder={t("username") || ""} fullWidth />
          <CustomTextField placeholder={t("email") || ""} fullWidth />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomTextField placeholder={t("subject") || ""} fullWidth />
          </Box>
          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomTextField
              placeholder={t("message") || ""}
              fullWidth
              multiline
              rows={5}
            />
          </Box>
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Button size="lg" sx={{ px: 4 }}>
              {t("sendMessage")}
            </Button>
          </Box>
        </Box>
      </Card>
    </Stack>
  );
};

const SectionHeader = ({ title, eyebrow }: { title: string; eyebrow: string }) => {
  return (
    <Stack spacing={1}>
      <Chip color="primary" variant="soft" sx={{ width: "fit-content", borderRadius: 999 }}>
        {eyebrow}
      </Chip>
      <Typography level="h2">{title}</Typography>
      <Stack spacing={0.6}>
        <Box sx={{ height: 5, width: 88, borderRadius: 999, bgcolor: "primary.500" }} />
        <Box sx={{ height: 5, width: 52, borderRadius: 999, bgcolor: "primary.300" }} />
      </Stack>
    </Stack>
  );
};

export default Contact;
